import { CSSProperties, ReactNode } from "react"
import { CohortSyncPathCohortFragment, ResourceFragment } from "@pathwright/graphql-client/src/generated"
import BackgroundImage, { BackgroundImageProps } from "./BackgroundImage"

// Support both resource types - some have blurred_background, others don't
type ResourceBackgroundResource =
  | CohortSyncPathCohortFragment["resource"]
  | ResourceFragment
  | null

type ResourceBackgroundProps = {
  resource: ResourceBackgroundResource
  className?: string
  style?: CSSProperties
  children?: ReactNode
  overlay?: string
  forceBlur?: boolean
} & Omit<BackgroundImageProps, 'image' | 'overlay' | 'children'>

// Type guard to check if resource has blurred_background field
const hasBlurredBackground = (
  resource: NonNullable<ResourceBackgroundResource>
): resource is NonNullable<ResourceBackgroundResource> & { blurred_background: string } => {
  return 'blurred_background' in resource && Boolean(resource.blurred_background)
}

const ResourceBackground = ({
  resource,
  overlay,
  forceBlur,
  children,
  ...backgroundImageProps
}: ResourceBackgroundProps) => {
  // Early return if resource is null/undefined
  if (!resource) {
    return null
  }

  // Determine which image to use based on forceBlur and available fields
  const image = forceBlur && hasBlurredBackground(resource)
    ? resource.blurred_background
    : resource.background_image || undefined

  // Use resource's background overlay if none provided
  const finalOverlay = overlay || resource.background_overlay || undefined

  return (
    <BackgroundImage
      image={image}
      overlay={finalOverlay}
      className="ResourceBackground BackgroundImage"
      {...backgroundImageProps}
    >
      {children}
    </BackgroundImage>
  )
}

ResourceBackground.displayName = "ResourceBackground"

export default ResourceBackground
